import React, { useState, useEffect, useCallback } from 'react';
import './AdminStats.css';
import API_BASE_URL from '../config/api';

const AdminStats = () => {
  const [stats, setStats] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const events = [
    "Binary Runes",
    "Arduino Alchemy",
    "Hex Track",
    "Runic Maze",
    "Project Nexus",
    "Construct Clash",
    "Arcane Quiz",
    "Zaun Race",
    "BGMI Rush",
  ];

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      let query = `${API_BASE_URL}/api/admin-stats?`;
      if (filterEvent) query += `event=${encodeURIComponent(filterEvent)}&`;
      if (filterStatus) query += `paymentStatus=${encodeURIComponent(filterStatus)}`;

      const res = await fetch(query);
      const data = await res.json();

      if (data.success) {
        setStats(data.data);
        setTotalCount(data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching admin stats:", error);
    } finally {
      setLoading(false);
    }
  }, [filterEvent, filterStatus]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle Search Filtering
  const filteredStats = stats.filter((member) => {
    const query = searchQuery.toLowerCase();
    return (
      (member.name || '').toLowerCase().includes(query) ||
      (member.email || '').toLowerCase().includes(query) ||
      (member.usn || '').toLowerCase().includes(query) ||
      (member.phone || '').toLowerCase().includes(query) ||
      (member.bgmiid || '').toLowerCase().includes(query) ||
      (member.bgmiAccountId || '').toLowerCase().includes(query)
    );
  });

  // Highlight matched text utility
  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = String(text).split(new RegExp(`(${highlight})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === highlight.toLowerCase() ? (
        <span key={index} className="highlight-text">{part}</span>
      ) : (
        part
      )
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration? This action cannot be undone.")) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin-stats/${id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(stats.filter((member) => member._id !== id));
        setTotalCount(prev => prev - 1);
        alert("Member successfully deleted.");
      } else {
        alert(data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting member:", error);
      alert("Error deleting member");
    }
  };

  const exportToCSV = () => {
    if (filteredStats.length === 0) return alert("No data to export!");

    const headers = ['Name', 'Email', 'USN', 'Phone', 'BGMI Ingame Name', 'BGMI ID', 'Event', 'Payment Status', 'Timestamp'];
    
    const csvRows = filteredStats.map(member => {
      const timestamp = new Date(member._id.substring(0, 8) * 1000).toLocaleString();
      return [
        member.name || '',
        member.email || '',
        member.usn || '',
        member.phone || '',
        member.bgmiid || '',
        member.bgmiAccountId || '',
        member.event || '',
        member.paymentStatus || '',
        timestamp
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'robocor_registrations.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="admin-stats-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Admin Dashboard - Registrations</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={exportToCSV} 
            style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Download CSV / Excel
          </button>
          <a href="/admin-missing" style={{ color: '#0ea5e9', textDecoration: 'none', border: '1px solid #0ea5e9', padding: '8px 16px', borderRadius: '4px' }}>
            View Missing Claims
          </a>
          <a href="/admin-feedback" style={{ color: '#ec4899', textDecoration: 'none', border: '1px solid #ec4899', padding: '8px 16px', borderRadius: '4px' }}>
            View Feedback
          </a>
        </div>
      </div>
      
      <div className="filters-container">
        <div className="filter-group">
          <label>Search:</label>
          <input 
            type="text" 
            placeholder="Search Name, USN, Email..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Filter by Event:</label>
          <select value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
            <option value="">All Events</option>
            {events.map((event) => (
              <option key={event} value={event}>{event}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Filter by Payment Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        
        <div className="total-count-box">
          Total Found: <strong>{filteredStats.length}</strong>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>
            Base Total: {totalCount}
          </div>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading-state">Loading stats...</div>
        ) : (
          <table className="stats-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>USN</th>
                <th>Phone</th>
                <th>BGMI Ingame Name</th>
                <th>BGMI ID</th>
                <th>Event</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStats.length > 0 ? (
                filteredStats.map((member, index) => (
                  <tr key={member._id}>
                    <td>{index + 1}</td>
                    <td>{getHighlightedText(member.name, searchQuery)}</td>
                    <td>{getHighlightedText(member.email, searchQuery)}</td>
                    <td>{getHighlightedText(member.usn, searchQuery)}</td>
                    <td>{getHighlightedText(member.phone, searchQuery)}</td>
                    <td>{getHighlightedText(member.bgmiid || '-', searchQuery)}</td>
                    <td>{getHighlightedText(member.bgmiAccountId || '-', searchQuery)}</td>
                    <td><span className="event-badge">{member.event}</span></td>
                    <td>
                      <span className={`status-badge ${member.paymentStatus}`}>
                        {member.paymentStatus}
                      </span>
                    </td>
                    <td>{new Date(member._id.substring(0, 8) * 1000).toLocaleString()}</td>
                    <td>
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDelete(member._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="no-data">No matching records found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminStats;
